import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DangerousIcon from "@mui/icons-material/Dangerous";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  alpha,
  Badge,
  Box,
  Button,
  Chip,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import {
  DataGrid,
  GridActionsCellItem,
  GridToolbar,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { API, baseUrl } from "../../../api/API";
import { appColors } from "../../../constant/constant";
import { PRESCRIPTION_MEDECIN_CONESEIL_AVIS } from "../../../constant/GlobalConstant";
import useAuthStore from "../../../store/authStore";
import AddPrescription from "../modals/AddPrescription";
import CommentDialog from "../modals/CommentModal";
import RejectDialog from "../modals/RejectModal";
import ValideDialog from "../modals/ValideModal";
import ViewPrescription from "../modals/ViewPrescription";

const StatusButton = styled(Button)(({ theme }) => ({
  textTransform: "capitalize",
  fontWeight: 600,
  borderRadius: "16px",
  padding: "4px 12px",
  minWidth: "100px",
  fontSize: "0.75rem",
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: "16px",
  padding: "4px 8px",
  height: "auto",
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: -12,
    top: 10,
    padding: "0 6px",
    background: theme.palette.success.main,
    color: theme.palette.common.white,
  },
}));

// Composant TabPanel pour gérer l'affichage des onglets
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prescription-tabpanel-${index}`}
      aria-labelledby={`prescription-tab-${index}`}
      {...other}
      style={{ width: "100%" }}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const RenderStatu = ({ statu }) => {
  switch (statu?.toLowerCase()) {
    case "valider":
      return (
        <StatusButton
          color="success"
          variant="contained"
          startIcon={<CheckCircleOutlineIcon fontSize="small" />}
          sx={{
            backgroundColor: "#4caf50",
            "&:hover": {
              backgroundColor: "#388e3c",
            },
          }}
        >
          Validé
        </StatusButton>
      );

    case "rejeter":
      return (
        <StatusButton
          color="error"
          variant="outlined"
          size="small"
          startIcon={<CancelOutlinedIcon fontSize="small" />}
          sx={{
            borderColor: "#f44336",
            color: "#f44336",
          }}
        >
          Rejeté
        </StatusButton>
      );

    case "en_attente":
      return (
        <StatusButton
          color="warning"
          variant="outlined"
          startIcon={<AccessTimeOutlinedIcon fontSize="small" />}
          sx={{
            borderColor: "#ff9800",
            color: "#ff9800",
          }}
        >
          En attente
        </StatusButton>
      );

    default:
      return <StatusChip label="Inconnu" color="default" variant="outlined" />;
  }
};
const actionTypes = {
  voir: "voir",
  valide: "valide",
  rejeter: "rejeter",
  addComment: "Ajouter un commentaire",
};

const iconList = [
  <VisibilityOutlinedIcon sx={{ color: appColors.secondary }} />,
  <TipsAndUpdatesIcon color="success" />,
  <DangerousIcon sx={{ color: appColors.danger }} />,
  <EditOutlinedIcon sx={{ color: appColors.secondary }} />,
  <DeleteOutlineOutlinedIcon sx={{ color: appColors.danger }} />,
];

const DataGridPrescriptionViewFromAgent = () => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50 });
  const [rows, setRows] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  // Modal states
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isValide, setisValide] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);

  const user = useAuthStore((state) => state.user);
  const role = user ? user.role : null;

  const handleAction = useCallback((selectedRow, actionType) => {
    setSelectedRow(selectedRow);
    switch (actionType) {
      case actionTypes.voir:
        setIsViewModalOpen(true);
        break;
      case actionTypes.valide:
        setisValide(true);
        break;
      case actionTypes.rejeter:
        setIsDeleteModalOpen(true);
        break;
      case actionTypes.addComment:
        setIsAddCommentModalOpen(true);
        break;
      default:
        break;
    }
  }, []);

  // Séparation des données par statut
  const pendingPrescriptions = useMemo(
    () =>
      rows.filter(
        (row) => row.avis_agent_assurance?.toLowerCase() === "en_attente"
      ),
    [rows]
  );

  const processedPrescriptions = useMemo(
    () =>
      rows.filter(
        (row) => row.avis_agent_assurance?.toLowerCase() !== "en_attente"
      ),
    [rows]
  );

  const validatedPrescriptions = useMemo(
    () =>
      rows.filter(
        (row) => row.avis_agent_assurance?.toLowerCase() === "valider"
      ),
    [rows]
  );

  const rejectedPrescriptions = useMemo(
    () =>
      rows.filter(
        (row) => row.avis_agent_assurance?.toLowerCase() === "rejeter"
      ),
    [rows]
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRow) return;
    try {
      const res = await API.delete(`/prescriptions/${selectedRow.id}`);
      if (res.status === 200 || res.status === 204) {
        handleRefresh();
        setIsDeleteModalOpen(false);
      }
    } catch (error) {
      console.error(error.message);
      setIsDeleteModalOpen(false);
    }
  };

  const handleOpenFile = (file_name) => {
    if (file_name) {
      const urlToOpen = `${baseUrl}/media/${file_name}`;
      window.open(urlToOpen, "_blank", "noopener,noreferrer");
    } else {
      console.warn("Aucune URL de fichier fournie pour cette ligne.");
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "order",
        headerName: "#",
        width: 80,
      },
      {
        field: "numero",
        headerName: "Numéro",
        width: 150,
      },
      {
        field: "titre",
        headerName: "Titre",
        width: 200,
      },
      {
        field: "description",
        headerName: "Description",
        width: 200,
      },
      {
        field: "type",
        headerName: "Type",
        width: 120,
      },
      {
        field: "avis_agent_assurance",
        headerName: "Statut",
        width: 200,
        renderCell: (params) => <RenderStatu statu={params.value} />,
      },
      {
        field: "comment_agent_assurance",
        headerName: "Commentaire",
        width: 200,
      },
      {
        field: "matricule_assure",
        headerName: "Matricule Assuré",
        width: 150,
      },
      {
        field: "createdAt",
        headerName: "Créé le",
        width: 120,
        valueGetter: (value) => new Date(value).toLocaleDateString(),
      },
      {
        field: "image",
        headerName: "Fichier",
        width: 160,
        renderCell: (item) => {
          const file = item.value;
          if (!file) return null;

          return (
            <Button
              onClick={() => handleOpenFile(file)}
              variant="outlined"
              size="small"
              sx={{
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              Ouvrir
            </Button>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        width: 120,
        disableExport: true,
        getActions: (params) => {
          const currentStatus = params.row.avis_agent_assurance?.toLowerCase();

          return Object.entries(actionTypes).map(([key, value], index) => {
            let disabled = false;

            if (value === actionTypes.valide && currentStatus === "valider") {
              disabled = true;
            }

            if (value === actionTypes.rejeter && currentStatus === "rejeter") {
              disabled = true;
            }

            return (
              <GridActionsCellItem
                icon={iconList[index]}
                label={value}
                key={key}
                onClick={() => !disabled && handleAction(params.row, value)}
                showInMenu
                disabled={disabled}
                slotProps={{
                  tooltip: {
                    title: disabled
                      ? `Cette action n'est pas disponible pour une prescription ${currentStatus}`
                      : value,
                  },
                }}
              />
            );
          });
        },
      },
    ],
    [handleAction, theme]
  );

  const fetchPrescriptions = () => {
    setIsLoading(true);
    API.get(`/prescriptions?request_from=${role} `)
      .then((response) => {
        const data = response.data.data.map((row, index) => ({
          ...row,
          order: index + 1,
        }));
        setRows(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error.message);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchPrescriptions();
  }, []);

  const handleRefresh = () => {
    setIsLoading(true);
    fetchPrescriptions();
  };

  const handleCommentSubmit = async (comment) => {
    if (!selectedRow) return;
    try {
      const res = await API.patch(
        `/prescriptions/${selectedRow.id}?comment_agent_assurance=${comment}`
      );
      if (res.status === 200) {
        handleRefresh();
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ backgroundColor: "#f5f5f5", color: "#37B049" }}>
      <GridToolbar
        sx={{
          "& .MuiButton-root": {
            color: "#37B049",
          },
          "& .MuiSvgIcon-root": {
            color: "#37B049",
          },
        }}
      />
      <AddPrescription onRefresh={handleRefresh} />
    </GridToolbarContainer>
  );

  const handleValide = async () => {
    if (!selectedRow) return;
    try {
      const res = await API.patch(
        `/prescriptions/${selectedRow.id}?avis_agent_assurance=${PRESCRIPTION_MEDECIN_CONESEIL_AVIS.valider}`
      );
      if (res.status === 200) {
        handleRefresh();
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  const handleRejete = async () => {
    if (!selectedRow) return;
    try {
      const res = await API.patch(
        `/prescriptions/${selectedRow.id}?avis_agent_assurance=${PRESCRIPTION_MEDECIN_CONESEIL_AVIS.rejeter} `
      );
      if (res.status === 200 || res.status === 204) {
        handleRefresh();
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  // Style commun pour les DataGrid
  const dataGridStyle = {
    borderRadius: 2,
    boxShadow: "0 4px 12px 0 rgba(0,0,0,0.05)",
    border: "none",
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: alpha(theme.palette.success.light, 0.15),
      color: theme.palette.success.dark,
    },
    "& .MuiDataGrid-cell:focus": {
      outline: "none",
    },
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        elevation={2}
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              fontWeight: 600,
              fontSize: "0.9rem",
              py: 2,
            },
          }}
        >
          <Tab
            icon={<AccessTimeOutlinedIcon />}
            iconPosition="start"
            label={
              <StyledBadge
                badgeContent={pendingPrescriptions.length}
                color="warning"
              >
                <Box sx={{ mr: 3 }}>En attente</Box>
              </StyledBadge>
            }
            id="prescription-tab-0"
            aria-controls="prescription-tabpanel-0"
          />
          <Tab
            icon={<CheckCircleOutlineIcon />}
            iconPosition="start"
            label={
              <StyledBadge
                badgeContent={validatedPrescriptions.length}
                color="success"
              >
                <Box sx={{ mr: 3 }}>Validées</Box>
              </StyledBadge>
            }
            id="prescription-tab-1"
            aria-controls="prescription-tabpanel-1"
          />
          <Tab
            icon={<CancelOutlinedIcon />}
            iconPosition="start"
            label={
              <StyledBadge
                badgeContent={rejectedPrescriptions.length}
                color="error"
              >
                <Box sx={{ mr: 3 }}>Rejetées</Box>
              </StyledBadge>
            }
            id="prescription-tab-2"
            aria-controls="prescription-tabpanel-2"
          />
          <Tab
            icon={<VisibilityOutlinedIcon />}
            iconPosition="start"
            label="Toutes"
            id="prescription-tab-3"
            aria-controls="prescription-tabpanel-3"
          />
        </Tabs>

        {/* Onglet En attente */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Prescriptions en attente de traitement
            </Typography>
            <DataGrid
              slots={{
                toolbar: CustomToolbar,
              }}
              columns={columns}
              rows={pendingPrescriptions}
              checkboxSelection
              disableRowSelectionOnClick
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: pagination.limit,
                  },
                },
              }}
              pageSizeOptions={[pagination.limit]}
              loading={isLoading}
              autoHeight
              sx={dataGridStyle}
            />
          </Box>
        </TabPanel>

        {/* Onglet Validées */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Prescriptions validées
            </Typography>
            <DataGrid
              slots={{
                toolbar: CustomToolbar,
              }}
              columns={columns}
              rows={validatedPrescriptions}
              checkboxSelection
              disableRowSelectionOnClick
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: pagination.limit,
                  },
                },
              }}
              pageSizeOptions={[pagination.limit]}
              loading={isLoading}
              autoHeight
              sx={dataGridStyle}
            />
          </Box>
        </TabPanel>

        {/* Onglet Rejetées */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Prescriptions rejetées
            </Typography>
            <DataGrid
              slots={{
                toolbar: CustomToolbar,
              }}
              columns={columns}
              rows={rejectedPrescriptions}
              checkboxSelection
              disableRowSelectionOnClick
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: pagination.limit,
                  },
                },
              }}
              pageSizeOptions={[pagination.limit]}
              loading={isLoading}
              autoHeight
              sx={dataGridStyle}
            />
          </Box>
        </TabPanel>

        {/* Onglet Toutes */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Toutes les prescriptions
            </Typography>
            <DataGrid
              slots={{
                toolbar: CustomToolbar,
              }}
              columns={columns}
              rows={rows}
              checkboxSelection
              disableRowSelectionOnClick
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: pagination.limit,
                  },
                },
              }}
              pageSizeOptions={[pagination.limit]}
              loading={isLoading}
              autoHeight
              sx={dataGridStyle}
            />
          </Box>
        </TabPanel>
      </Paper>

      {/* Modales */}
      {isViewModalOpen && selectedRow && (
        <ViewPrescription
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          prescription={selectedRow}
        />
      )}

      {isValide && selectedRow && (
        <ValideDialog
          isValideDialogOpen={isValide}
          setIsValideDialogOpen={() => setisValide(false)}
          handleDeleteConfirm={handleValide}
          message="Voulez vous valider cette prescription"
        />
      )}

      {isDeleteModalOpen && selectedRow && (
        <RejectDialog
          isRejectDialogOpen={isDeleteModalOpen}
          setIsRejectDialogOpen={() => setIsDeleteModalOpen(false)}
          handleDeleteConfirm={handleRejete}
          message="Voulez vous rejeter cette prescription"
        />
      )}
      {isAddCommentModalOpen && selectedRow && (
        <CommentDialog
          isOpen={isAddCommentModalOpen}
          onClose={() => setIsAddCommentModalOpen(false)}
          handleComment={handleCommentSubmit}
          message="Ajouter un commentaire"
        />
      )}
    </Box>
  );
};

export default DataGridPrescriptionViewFromAgent;
